/**
 * Viewer: Frames empfangen, Bild rekonstruieren, Empfangsbereitschaft melden.
 *
 * Das `ready`-Signal ist zugleich Standmeldung und Guthaben: Der Server sendet
 * erst wieder, wenn dieser Viewer verarbeitet hat. Wer zurückfällt, bekommt
 * die verpassten Frames zu einem einzigen Delta zusammengefasst statt einzeln.
 */

import { FRAME_DIFF, FRAME_KEY, decodeFrame, type ServerMessage } from '../../shared/protocol.ts';
import { ServerClock } from '../../shared/clock.ts';
import { Renderer } from './renderer.ts';

const room = new URLSearchParams(location.search).get('room') ?? 'test';

const ui = {
  status: document.querySelector<HTMLElement>('#status')!,
  overlay: document.querySelector<HTMLElement>('#overlay')!,
  notice: document.querySelector<HTMLElement>('#notice')!,
  stage: document.querySelector<HTMLElement>('#stage')!,
  canvas: document.querySelector<HTMLCanvasElement>('#screen')!,
};

const renderer = new Renderer(ui.canvas, ui.stage);

let socket: WebSocket | null = null;
let lastFrameId = 0;
/** Uhr des Servers – die gemeinsame Bezugsgröße für die Latenzmessung. */
const clock = new ServerClock(send);

/** Aufeinanderfolgende Fehlschläge; danach wird ein Vollbild angefordert. */
let frameFailures = 0;
const MAX_FRAME_FAILURES = 3;
/** Wartezeit, bevor nach einem Fehlschlag erneut angefordert wird. */
const RETRY_DELAY = 500;
/** Ab dieser Länge (Sekunden) gilt der Messzeitraum als von einer Pause zerrissen. */
const IDLE_WINDOW = 2;

const stats = { frames: 0, bytes: 0, tiles: 0, latency: 0, keyframes: 0 };
let windowBytes = 0;
let windowFrames = 0;
let windowStart = performance.now();

function connect(): void {
  const url = new URL('/ws', location.href);
  url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  socket = new WebSocket(url);
  socket.binaryType = 'arraybuffer';

  socket.addEventListener('open', () => {
    ui.status.textContent = 'verbunden';
    send({ t: 'hello', role: 'viewer', room });
    clock.start();
    // Stand melden: 0 beim ersten Mal, nach Reconnect der letzte bekannte Frame.
    sendReady();
  });

  socket.addEventListener('message', (event) => {
    if (typeof event.data === 'string') {
      handleControl(JSON.parse(event.data) as ServerMessage);
    } else {
      void handleFrame(new Uint8Array(event.data as ArrayBuffer));
    }
  });

  socket.addEventListener('close', () => {
    clock.stop();
    clock.reset();
    ui.status.textContent = 'getrennt – neuer Versuch in 2 s';
    showNotice('Verbindung getrennt – neuer Versuch in 2 s');
    socket = null;
    setTimeout(connect, 2000);
  });
}

/** Kein laufender Strom: Bild zurücktreten lassen und den Grund nennen. */
function showNotice(text: string): void {
  ui.notice.textContent = text;
  ui.notice.hidden = false;
  ui.stage.classList.add('inactive');
}

function hideNotice(): void {
  // Läuft bei jedem Frame – die DOM-Zugriffe nur, wenn es etwas wegzuräumen gibt.
  if (ui.notice.hidden) return;
  ui.notice.hidden = true;
  ui.stage.classList.remove('inactive');
}

const NOTICE_TEXT: Record<string, string> = {
  stopped: 'Freigabe beendet',
  timeout: 'Übertragung abgebrochen',
  disconnected: 'Sender hat die Verbindung verlassen',
  idle: 'warte auf Freigabe …',
};

function handleControl(message: ServerMessage): void {
  if (message.t === 'time') {
    clock.accept(message);
  } else if (message.t === 'meta') {
    renderer.setGeometry(message.width, message.height);
    ui.status.textContent = `${message.width}×${message.height}`;
    // Neue Geometrie: alles bisherige ist ungültig, Vollbild anfordern.
    lastFrameId = 0;
    sendReady();
  } else if (message.t === 'senderGone') {
    const text = NOTICE_TEXT[message.reason ?? 'idle'] ?? NOTICE_TEXT.idle!;
    ui.status.textContent = text;
    showNotice(text);
    lastFrameId = 0;
  } else if (message.t === 'viewerCount') {
    // Der Sender zählt sich nicht mit.
  }
}

/**
 * Ein Frame verarbeiten.
 *
 * Der ganze Rumpf steht in einem try: Das Guthaben ist die einzige
 * Aufforderung an den Server, weiterzusenden – bleibt `sendReady()` wegen eines
 * Fehlers aus, warten Server und Viewer für immer aufeinander, und beim
 * Zuschauer bleibt das letzte Bild ohne jeden Hinweis stehen.
 */
async function handleFrame(bytes: Uint8Array): Promise<void> {
  const t0 = performance.now();
  try {
    const frame = decodeFrame(bytes);

    await renderer.apply(frame);
    // Es läuft wieder etwas – ein Neustart des Senders räumt den Hinweis selbst weg.
    hideNotice();

    lastFrameId = frame.frameId;
    frameFailures = 0;
    stats.frames++;
    stats.bytes += bytes.length;
    windowBytes += bytes.length;
    windowFrames++;
    stats.tiles = frame.type === FRAME_DIFF ? frame.lossyTiles.length + frame.losslessTiles.length : -1;
    if (frame.type === FRAME_KEY) stats.keyframes++;
    // Beide Seiten stempeln in Serverzeit – erst dadurch ist die Differenz
    // zweier Rechner überhaupt eine Latenz.
    stats.latency = clock.now() - frame.timestamp + (performance.now() - t0);
  } catch (err) {
    recoverFromFrameError(err);
    return;
  }

  // Erst nach dem Rendern neues Guthaben geben – so entsteht kein Rückstau.
  sendReady();
  updateOverlay();
}

/**
 * Nach einem verworfenen Frame wieder in Gang kommen.
 *
 * `lastFrameId` bleibt zunächst stehen: Der Server fasst dann alles seit diesem
 * Stand zu einem Delta zusammen, die verlorenen Kacheln kommen also erneut.
 * Scheitert das mehrfach, liegt es vermutlich nicht am einzelnen Frame – dann
 * wird mit Stand 0 ein Vollbild angefordert und neu aufgesetzt.
 */
function recoverFromFrameError(err: unknown): void {
  frameFailures++;
  console.error(`[viewer] Frame verworfen (${frameFailures}):`, err);

  if (frameFailures >= MAX_FRAME_FAILURES) {
    frameFailures = 0;
    lastFrameId = 0;
  }
  // Nicht sofort nachfordern: Bei einem dauerhaften Fehler entstünde sonst eine
  // enge Schleife aus Anfordern und Scheitern über die Leitung.
  setTimeout(sendReady, RETRY_DELAY);
  updateOverlay();
}

function sendReady(): void {
  send({ t: 'ready', lastFrameId });
}

function send(message: object): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

function updateOverlay(): void {
  const now = performance.now();
  const elapsed = (now - windowStart) / 1000;
  if (elapsed < 0.5) return;

  // Nach einer Pause ist der Zeitraum wertlos: Ein einzelnes Bild nach zehn
  // Sekunden Stillstand ergäbe "0,1 fps" und läse sich wie ein
  // Leistungsproblem – dabei war schlicht nichts zu übertragen.
  const stale = elapsed > IDLE_WINDOW;
  const fps = stale ? '–' : (windowFrames / elapsed).toFixed(1);
  const kbps = stale ? '–' : ((windowBytes * 8) / elapsed / 1000).toFixed(0);
  windowFrames = 0;
  windowBytes = 0;
  windowStart = now;

  ui.overlay.innerHTML = `
    <div><b>${fps}</b> fps &middot; <b>${kbps}</b> kbit/s</div>
    <div>Kacheln: ${stats.tiles < 0 ? 'Vollbild' : stats.tiles}</div>
    <div>Latenz: ${
      clock.synced ? `~${Math.max(0, stats.latency).toFixed(0)} ms (±${(clock.roundTrip / 2).toFixed(0)})` : '– (Uhren noch nicht abgeglichen)'
    }</div>
    <div>Frames: ${stats.frames} &middot; Vollbilder: ${stats.keyframes}</div>
    <div>Frame-Nr: ${lastFrameId}</div>
  `;
}

let resizeTimer = 0;
function scheduleResize(): void {
  clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => renderer.resize(), 150);
}

window.addEventListener('resize', scheduleResize);
// Nicht nur am Fenster hängen: Die Bühne ändert ihre Größe auch, wenn das
// Fenster gleich bleibt – Layoutwechsel, Zoom, ein- und ausgeblendete Hinweise.
new ResizeObserver(scheduleResize).observe(ui.stage);

/**
 * Wechsel auf einen Monitor mit anderer Pixeldichte. Dabei bleibt die CSS-Größe
 * gleich, das Canvas behielte also seine Pixelmaße und das Bild würde weich.
 * Die Abfrage gilt für genau einen Wert und wird nach jedem Wechsel neu gestellt.
 */
function watchPixelRatio(): void {
  matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener(
    'change',
    () => {
      scheduleResize();
      watchPixelRatio();
    },
    { once: true },
  );
}
watchPixelRatio();

connect();
